import {
  LocationSummaryColumnKey,
  SortDirection,
} from '../constants/locationSummaryColumns';
import {
  LocationRecord,
  MarketRecord,
  RegionRecord,
  WeekRecord,
} from '../constants/locationSummaryMockData';
import { compareValues } from './sortingHelpers';

export type RowType = 'location' | 'week';

export interface FlatLocationSummaryRow {
  rowKey: string;
  rowType: RowType;
  level: number;
  hasChildren: boolean;
  expanded: boolean;
  region: string;
  market: string;
  location: string;
  created: number;
  target: number;
  ptExecution: number;
  approved: number;
  pending: number;
  declined: number;
}

const getRowValues = (
  rowType: RowType,
  item: LocationRecord | WeekRecord,
  context: { region: string; market: string; location: string },
): Record<LocationSummaryColumnKey, string | number> => {
  if (rowType === 'week') {
    const week = item as WeekRecord;
    return {
      region: context.region,
      market: context.market,
      location: week.weekLabel,
      created: week.created,
      target: week.target,
      ptExecution: week.ptExecution,
      approved: week.approved,
      pending: week.pending,
      declined: week.declined,
    };
  }

  const location = item as LocationRecord;
  return {
    region: context.region,
    market: context.market,
    location: location.name,
    created: location.created,
    target: location.target,
    ptExecution: location.ptExecution,
    approved: location.approved,
    pending: location.pending,
    declined: location.declined,
  };
};

const sortCollection = <T extends LocationRecord | WeekRecord>(
  items: T[],
  rowType: RowType,
  sortKey: LocationSummaryColumnKey,
  sortDirection: SortDirection,
  context: { region: string; market: string; location: string },
): T[] => {
  const sorted = [...items];
  sorted.sort((left, right) => {
    const leftVal = getRowValues(rowType, left, context)[sortKey];
    const rightVal = getRowValues(rowType, right, context)[sortKey];
    return compareValues(leftVal, rightVal, sortDirection);
  });
  return sorted;
};

export const flattenAndSortLocations = (
  data: RegionRecord[],
  sortKey: LocationSummaryColumnKey,
  sortDirection: SortDirection,
): Array<{ region: string; market: string; location: LocationRecord }> => {
  const flattened: Array<{ region: string; market: string; location: LocationRecord }> = [];
  data.forEach((region) => {
    region.markets.forEach((market) => {
      market.locations.forEach((location) => {
        flattened.push({
          region: region.name,
          market: market.name,
          location,
        });
      });
    });
  });

  const sorted = [...flattened];
  sorted.sort((left, right) => {
    const leftContext = { region: left.region, market: left.market, location: left.location.name };
    const rightContext = { region: right.region, market: right.market, location: right.location.name };
    const leftVal = getRowValues('location', left.location, leftContext)[sortKey];
    const rightVal = getRowValues('location', right.location, rightContext)[sortKey];
    return compareValues(leftVal, rightVal, sortDirection);
  });

  return sorted;
};

export const buildFlatRows = (
  flattenedLocations: Array<{ region: string; market: string; location: LocationRecord }>,
  expandedRows: Record<string, boolean>,
): FlatLocationSummaryRow[] => {
  const rows: FlatLocationSummaryRow[] = [];

  flattenedLocations.forEach(({ region, market, location }) => {
    const locationKey = `location:${location.id}`;
    const locationExpanded = Boolean(expandedRows[locationKey]);

    rows.push({
      rowKey: locationKey,
      rowType: 'location',
      level: 0,
      hasChildren: location.weeks.length > 0,
      expanded: locationExpanded,
      region,
      market,
      location: location.name,
      created: location.created,
      target: location.target,
      ptExecution: location.ptExecution,
      approved: location.approved,
      pending: location.pending,
      declined: location.declined,
    });

    if (!locationExpanded) return;

    // Preserve API response order — do not re-sort weeks
    location.weeks.forEach((week) => {
      rows.push({
        rowKey: `week:${location.id}:${week.id}`,
        rowType: 'week',
        level: 1,
        hasChildren: false,
        expanded: false,
        region: '',
        market: '',
        location: week.weekLabel,
        created: week.created,
        target: week.target,
        ptExecution: week.ptExecution,
        approved: week.approved,
        pending: week.pending,
        declined: week.declined,
      });
    });
  });

  return rows;
};
